/**
 * Standard Actions configuration for Dawn.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

(function initChoicesTryOnWaitExperience() {
  const messages = [
    'Analyzing your photo and garment…',
    'Building the fit and drape…',
    'Refining garment details…',
    'Finishing your preview…'
  ];

  function setup(widget) {
    if (!widget || widget.dataset.waitExperienceReady === '1') return;
    widget.dataset.waitExperienceReady = '1';

    const loadingPanel = widget.querySelector('[data-panel="loading"]');
    const loadingMessage = widget.querySelector('[data-loading-message]');
    const actionButtons = () => widget.querySelectorAll('[data-generate], [data-regenerate], .choices-result__generate-size, [data-angle]');
    if (!loadingPanel || !loadingMessage) return;

    let timer = null;
    let index = 0;

    const update = () => {
      loadingMessage.textContent = `${messages[index % messages.length]} This usually takes 20–40 seconds. Please keep this window open.`;
      index += 1;
    };

    const setBusy = (busy) => {
      widget.setAttribute('aria-busy', String(busy));
      actionButtons().forEach((button) => {
        if (busy) {
          button.dataset.waitDisabled = button.disabled ? '1' : '0';
          button.disabled = true;
        } else if (button.dataset.waitDisabled === '0') {
          button.disabled = false;
          delete button.dataset.waitDisabled;
        }
      });
    };

    const sync = () => {
      const visible = !loadingPanel.hidden;
      if (visible && !timer) {
        index = 0;
        update();
        setBusy(true);
        timer = window.setInterval(update, 8000);
      } else if (!visible && timer) {
        window.clearInterval(timer);
        timer = null;
        setBusy(false);
      }
    };

    new MutationObserver(sync).observe(loadingPanel, {
      attributes: true,
      attributeFilter: ['hidden']
    });
    sync();
  }

  const start = () => {
    document.querySelectorAll('choices-try-on').forEach(setup);
    new MutationObserver(() => {
      document.querySelectorAll('choices-try-on').forEach(setup);
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/**
 * Choices owner-only AI provider badge.
 * Enable once with ?choices_ai_debug=1. Disable with ?choices_ai_debug=0.
 */
(function initChoicesAiDebugBadge() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('choices_ai_debug') === '1') {
    localStorage.setItem('choicesAiDebug', '1');
  } else if (params.get('choices_ai_debug') === '0') {
    localStorage.removeItem('choicesAiDebug');
  }

  if (localStorage.getItem('choicesAiDebug') !== '1') return;

  const BADGE_ID = 'choices-ai-provider-badge';

  function getBadge() {
    let badge = document.getElementById(BADGE_ID);
    if (badge) return badge;

    badge = document.createElement('div');
    badge.id = BADGE_ID;
    badge.setAttribute('role', 'status');
    badge.setAttribute('aria-live', 'polite');
    Object.assign(badge.style, {
      position: 'fixed',
      left: '12px',
      bottom: '12px',
      zIndex: '2147483647',
      padding: '8px 11px',
      borderRadius: '999px',
      background: 'rgba(14,14,14,.92)',
      color: '#fff',
      font: '600 12px/1.2 -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',
      letterSpacing: '.02em',
      boxShadow: '0 4px 18px rgba(0,0,0,.28)',
      border: '1px solid rgba(255,255,255,.18)',
      pointerEvents: 'auto',
      cursor: 'default',
      maxWidth: 'calc(100vw - 24px)'
    });
    badge.textContent = 'AI: Ready';
    badge.title = 'Owner-only AI provider monitor. Add ?choices_ai_debug=0 to disable.';
    document.body.appendChild(badge);
    return badge;
  }

  function setBadge(text, state, title) {
    const badge = getBadge();
    badge.textContent = text;
    badge.dataset.state = state || '';
    badge.title = title || 'Owner-only AI provider monitor';
    badge.style.background = state === 'openai'
      ? 'rgba(10,92,54,.94)'
      : state === 'fashn'
        ? 'rgba(98,57,8,.94)'
        : state === 'error'
          ? 'rgba(132,24,24,.94)'
          : 'rgba(14,14,14,.92)';
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function choicesAiObservedFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isAiRequest = url.includes('/app-proxy/generate') || url.includes('/app-proxy/angle');
    if (!isAiRequest) return originalFetch(input, init);

    const startedAt = performance.now();
    setBadge('AI: Generating…', 'loading');

    try {
      const response = await originalFetch(input, init);
      const elapsed = ((performance.now() - startedAt) / 1000).toFixed(1);
      let data = null;
      try {
        data = await response.clone().json();
      } catch (_) {
        data = null;
      }

      const providerHeader = (response.headers.get('X-Choices-AI-Provider') || '').toLowerCase();
      const fallbackHeader = (response.headers.get('X-Choices-AI-Fallback') || '').toLowerCase();
      const predictionId = String(data?.prediction_id || '');
      const provider = String(data?.provider || providerHeader || '').toLowerCase();

      if (provider === 'openai' || predictionId.startsWith('openai_')) {
        setBadge(`AI: OpenAI · ${elapsed}s`, 'openai', predictionId || 'OpenAI generation');
      } else if (fallbackHeader === 'fashn' || provider === 'fashn' || predictionId) {
        const label = fallbackHeader === 'fashn' ? 'FASHN fallback' : 'FASHN';
        setBadge(`AI: ${label} · ${elapsed}s`, 'fashn', predictionId || label);
      } else if (!response.ok) {
        setBadge(`AI: Error · ${elapsed}s`, 'error', data?.error || `HTTP ${response.status}`);
      } else {
        setBadge(`AI: Unknown · ${elapsed}s`, 'error', 'Provider marker was not returned');
      }

      return response;
    } catch (error) {
      const elapsed = ((performance.now() - startedAt) / 1000).toFixed(1);
      setBadge(`AI: Error · ${elapsed}s`, 'error', error?.message || 'Generation request failed');
      throw error;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', getBadge, { once: true });
  } else {
    getBadge();
  }
})();
